import type {ReactElement} from 'react';
import {useState} from 'react';
import {resolveChatMediaUrl} from '@Libraries/ai-chat.library';
import {MediaItemSizeType} from '@Libraries/s3-library';
import {ClickableDiv} from '@Components/clickable-div';
import {CustomFeedbackIcon} from '@Components/ai-chat-window/components/custom-feedback-icon/custom-feedback-icon';
import {ThumbsUpFilledIcon} from '@Components/ai-chat-window/components/thumbs-up-filled-icon/thumbs-up-filled-icon';
import {ThumbsDownFilledIcon} from '@Components/ai-chat-window/components/thumbs-down-filled-icon/thumbs-down-filled-icon';
import {Icon} from '@Components/icon-v2';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {isTouchDevice} from '@Utils/browser.util';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {useAiChatActions} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatMessage} from '@Components/ai-chat-window/providers/ai-chat-message.context';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {aiChatWindowActions} from '@Components/ai-chat-window/ai-chat-window.reducer';
import {MessageRole} from '@Components/ai-chat-window/ai-chat-window.types';
import {recordFeedback} from '@Libraries/ai-chat.library';
import type {EntityCardActionContext, RegisteredEntityCardActionSpec} from './actions/entity-card-action.types';
import type {EntityCardProps} from './entity-card.types';
import {THUMBS_DOWN_ICON, THUMBS_UP_ICON} from './entity-card.types';
import {getEntityCardAction} from './actions/entity-card-action-registry';
import {UseThisButton} from '@Components/ai-chat-window/components/use-this-button/use-this-button';
import {UsedAssetKind} from '@AiChat/callbacks/ai-chat-callbacks.types';
import styles from './entity-card.module.scss';

const META_SEPARATOR = '  ·  ';

export function EntityCard({title, meta, mediaUrl, icon, entityType, entity}: EntityCardProps): ReactElement {
  const actions = useAiChatActions();
  const {chatId, messageId} = useAiChatMessage();
  const dispatch = useAppDispatch();
  const message = useAppSelector((state) => state.aiChatWindow[chatId]?.messages?.[messageId]);
  const [runningActionIcon, setRunningActionIcon] = useState<string | null>(null);
  const action = getEntityCardAction(entityType);

  let feedback = null;
  if (message?.role === MessageRole.ASSISTANT) {
    feedback = message.feedback;
  }

  const actionContext: EntityCardActionContext = {
    openPdf: actions.openPdf,
    feedback,
    recordFeedback: (feedbackType): void => {
      void recordFeedback(chatId, messageId, feedbackType, null, null);

      if (message?.role !== MessageRole.ASSISTANT) {
        return;
      }

      dispatch(
        aiChatWindowActions.updateMessage({
          chatId,
          messageId,
          message: {...message, feedback: {type: feedbackType, comment: null, negativeFeedbackPreset: null}},
        })
      );
    },
  };

  const renderMedia = (): ReactElement | null => {
    if (mediaUrl !== undefined && mediaUrl !== '') {
      const resolvedMediaUrl = resolveChatMediaUrl(mediaUrl, MediaItemSizeType.THUMB);
      return (
        <div className={styles.media}>
          <img className={styles.mediaBackdrop} src={resolvedMediaUrl} alt="" aria-hidden="true" />
          <div className={styles.mediaBlur} aria-hidden="true" />
          <img className={styles.mediaImage} src={resolvedMediaUrl} alt={title ?? ''} />
        </div>
      );
    }

    if (icon !== undefined && icon !== '') {
      return (
        <div className={styles.iconThumb}>
          <i className={`${icon} ${styles.iconGlyph}`} aria-hidden="true" />
        </div>
      );
    }

    return null;
  };

  const renderTitle = (): ReactElement | null => {
    if (title === undefined || title === '') {
      return null;
    }
    return <Text val={title} size={TextSize.MD} bold className={styles.title} />;
  };

  const runCardAction = async (spec: RegisteredEntityCardActionSpec): Promise<void> => {
    setRunningActionIcon(spec.icon);

    try {
      await spec.run(entity ?? {}, actionContext);
    } finally {
      setRunningActionIcon(null);
    }
  };

  const renderSelectedThumb = (spec: RegisteredEntityCardActionSpec): ReactElement | null => {
    if (spec.icon === THUMBS_UP_ICON) {
      return <CustomFeedbackIcon cursorClassname="_cursor-pointer" tooltipText={window.i18next.t(spec.ariaLabelKey)} svg={<ThumbsUpFilledIcon />} />;
    }

    if (spec.icon === THUMBS_DOWN_ICON) {
      return <CustomFeedbackIcon cursorClassname="_cursor-pointer" tooltipText={window.i18next.t(spec.ariaLabelKey)} svg={<ThumbsDownFilledIcon />} />;
    }

    return null;
  };

  const renderCardAction = (spec: RegisteredEntityCardActionSpec): ReactElement => {
    const selectedThumb = renderSelectedThumb(spec);

    if (spec.isSelected?.(actionContext) === true && selectedThumb !== null) {
      return <div key={spec.icon}>{selectedThumb}</div>;
    }

    const label = window.i18next.t(spec.ariaLabelKey);

    return (
      <OverlayTrigger
        key={spec.icon}
        trigger={['hover', 'focus']}
        overlay={<Tooltip className={`spacing-m-b-1 _rounded-arrow ${isTouchDevice() ? '_invisible' : ''}`}>{label}</Tooltip>}
        delay={{show: 0, hide: 0}}
        placement="top"
        offset={undefined}
      >
        <Icon
          icon={spec.icon}
          size={IconSize.SIZE_ICON_20}
          type={IconType.GHOST}
          shape={IconShape.CIRCLE}
          ariaLabel={label}
          isLoading={runningActionIcon === spec.icon}
          onClick={(): void => {
            void runCardAction(spec);
          }}
        />
      </OverlayTrigger>
    );
  };

  const renderActions = (): ReactElement | null => {
    if (!action?.cardActions.length) {
      return null;
    }

    return (
      <div className={styles.actions}>
        {action.cardActions.map((spec) => {
          return renderCardAction(spec);
        })}
      </div>
    );
  };

  const renderUseThis = (): ReactElement | null => {
    if (entityType === undefined || entity === undefined) {
      return null;
    }

    return <UseThisButton asset={{kind: UsedAssetKind.ENTITY, chatId, entityType, entity}} />;
  };

  const renderContent = (): ReactElement => {
    return (
      <>
        {renderMedia()}
        <div className={styles.text}>
          {renderTitle()}
          <Text val={meta.join(META_SEPARATOR)} size={TextSize.MD} className={styles.meta} />
        </div>
      </>
    );
  };

  if (action === undefined || entity === undefined) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.root}>{renderContent()}</div>
        {renderActions()}
        {renderUseThis()}
      </div>
    );
  }

  const handleClick = (): void => {
    void action.run(entity, actionContext);
  };

  return (
    <div className={styles.wrapper}>
      <ClickableDiv className={`${styles.root} ${styles.clickable}`} onClick={handleClick}>
        {renderContent()}
      </ClickableDiv>
      {renderActions()}
      {renderUseThis()}
    </div>
  );
}
